import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Edit2, Trash2, Eye, EyeOff, Pin, PinOff,
  Search, Calendar, AlertCircle, CheckCircle, X,
  ChevronLeft, ChevronRight, Filter, RefreshCw,
  RotateCcw, Archive
} from 'lucide-react';
import { supabase, getSupabaseUrl } from '../../supabase/client';
import { formatDate } from '../../utils/timeUtils';

interface Notice {
  id: number;
  title: string;
  summary: string;
  content: string;
  link: string;
  link_type: 'internal' | 'external';
  category: string;
  is_pinned: boolean;
  is_highlighted: boolean;
  is_visible: boolean;
  status: 'draft' | 'published';
  publish_time: string;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

const categories = ['考试报名', '政策资讯', '系统公告', '活动通知'];

export default function AdminNotices() {
  const navigate = useNavigate();
  const [notices, setNotices] = useState<Notice[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<'all' | 'published' | 'draft'>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Notice | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingNotice, setEditingNotice] = useState<Notice | null>(null);
  const [showRecycleBin, setShowRecycleBin] = useState(false);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<Notice | null>(null);
  const pageSize = 10;

  const emptyForm = {
    title: '',
    summary: '',
    content: '',
    link: '',
    link_type: 'internal' as 'internal' | 'external',
    category: '考试报名',
    is_pinned: false,
    is_highlighted: false,
    is_visible: true,
    status: 'draft' as 'draft' | 'published',
    publish_time: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}T${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`; })()
  };

  // datetime-local 输入框需要本地时间格式（YYYY-MM-DDTHH:mm），这里只是给输入框的默认值
  // 实际写入数据库时会在 saveNotice 中通过 toISOString() 转换为带时区的 ISO 格式

  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    if (showRecycleBin) {
      fetchRecycleBin();
    } else {
      fetchNotices();
    }
  }, [currentPage, filterStatus, filterCategory, searchQuery, showRecycleBin]);

  const fetchNotices = async () => {
    setLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const authHeaders = session?.session ? { Authorization: `Bearer ${session.session.access_token}` } : {};

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          action: 'notices_list',
          page: currentPage,
          pageSize,
          filterStatus,
          filterCategory,
          searchQuery
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || '加载失败');

      setNotices(result.data || []);
      setTotalCount(result.count || 0);
    } catch (error: any) {
      alert('加载公告失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchRecycleBin = async () => {
    setLoading(true);
    try {
      const { data: session } = await supabase.auth.getSession();
      const authHeaders = session?.session ? { Authorization: `Bearer ${session.session.access_token}` } : {};

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          action: 'notices_recycle_bin',
          page: currentPage,
          pageSize,
          searchQuery
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || '加载失败');

      setNotices(result.data || []);
      setTotalCount(result.count || 0);
    } catch (error: any) {
      alert('加载回收站失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!restoreTarget) return;

    try {
      const { data: session } = await supabase.auth.getSession();
      const authHeaders = session?.session ? { Authorization: `Bearer ${session.session.access_token}` } : {};

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          action: 'notices_restore',
          id: restoreTarget.id
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || '恢复失败');

      alert('公告已恢复');
      setShowRestoreModal(false);
      setRestoreTarget(null);
      fetchRecycleBin();
    } catch (error: any) {
      alert('恢复失败: ' + error.message);
    }
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      alert('请输入公告标题');
      return;
    }

    try {
      const { data: session } = await supabase.auth.getSession();
      const authHeaders = session?.session ? { Authorization: `Bearer ${session.session.access_token}` } : {};

      const saveData = {
        ...formData,
        publish_time: (() => { const d = new Date(formData.publish_time); return isNaN(d.getTime()) ? null : d.toISOString(); })()
      };

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          action: editingNotice ? 'notices_update' : 'notices_create',
          id: editingNotice?.id,
          data: saveData
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || '保存失败');

      alert(editingNotice ? '公告更新成功' : '公告创建成功');
      setShowEditModal(false);
      setEditingNotice(null);
      setFormData(emptyForm);
      fetchNotices();
    } catch (error: any) {
      alert('保存失败: ' + error.message);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;

    try {
      const { data: session } = await supabase.auth.getSession();
      const authHeaders = session?.session ? { Authorization: `Bearer ${session.session.access_token}` } : {};

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          action: 'notices_delete',
          id: deleteTarget.id
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || '删除失败');

      alert('公告已删除');
      setShowDeleteModal(false);
      setDeleteTarget(null);
      await fetchNotices();
    } catch (error: any) {
      alert('删除失败: ' + (error.message || '未知错误'));
    }
  };

  const togglePin = async (notice: Notice) => {
    try {
      const { data: session } = await supabase.auth.getSession();
      const authHeaders = session?.session ? { Authorization: `Bearer ${session.session.access_token}` } : {};

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          action: 'notices_update',
          id: notice.id,
          data: { is_pinned: !notice.is_pinned }
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || '置顶失败');

      await fetchNotices();
    } catch (error: any) {
      alert('置顶失败: ' + (error.message || '未知错误'));
    }
  };

  const toggleStatus = async (notice: Notice) => {
    const newStatus = notice.status === 'published' ? 'draft' : 'published';
    try {
      const { data: session } = await supabase.auth.getSession();
      const authHeaders = session?.session ? { Authorization: `Bearer ${session.session.access_token}` } : {};

      const response = await fetch(`${getSupabaseUrl()}/functions/v1/message-service`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          action: 'notices_update',
          id: notice.id,
          data: { status: newStatus }
        })
      });

      const result = await response.json();
      if (!response.ok) throw new Error(result.error || '状态更新失败');

      await fetchNotices();
    } catch (error: any) {
      alert('操作失败: ' + (error.message || '未知错误'));
    }
  };

  const openEditModal = (notice?: Notice) => {
    if (notice) {
      setEditingNotice(notice);
      setFormData({
        title: notice.title,
        summary: notice.summary || '',
        content: notice.content || '',
        link: notice.link || '',
        link_type: notice.link_type || 'internal',
        category: notice.category || '考试报名',
        is_pinned: notice.is_pinned,
        is_highlighted: notice.is_highlighted,
        is_visible: notice.is_visible,
        status: notice.status,
        publish_time: notice.publish_time ? (() => { const d = new Date(notice.publish_time); return isNaN(d.getTime()) ? null : d.toISOString(); })() : (() => { const d = new Date(); return d.toISOString(); })()
      });
    } else {
      setEditingNotice(null);
      setFormData(emptyForm);
    }
    setShowEditModal(true);
  };

  const totalPages = Math.ceil(totalCount / pageSize);

  const getStatusBadge = (status: string) => {
    if (status === 'published') {
      return <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">已发布</span>;
    }
    return <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded text-xs font-medium">草稿</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {showRecycleBin ? '回收站' : '公告栏'}
              </h1>
              <p className="text-sm text-gray-500 mt-1">
                {showRecycleBin ? '查看和恢复已删除的公告' : '管理首页公告通知，支持置顶、高亮、草稿等功能'}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => {
                  setShowRecycleBin(!showRecycleBin);
                  setCurrentPage(1);
                  setSearchQuery('');
                }}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  showRecycleBin
                    ? 'bg-gray-600 text-white hover:bg-gray-700'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {showRecycleBin ? <RotateCcw className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                {showRecycleBin ? '返回公告栏' : '回收站'}
              </button>
              {!showRecycleBin && (
                <button
                  onClick={() => openEditModal()}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <Plus className="w-4 h-4" />
                  新增公告
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-6">
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-[200px] max-w-md">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="搜索公告标题..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              {!showRecycleBin && (
                <>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value as 'all' | 'published' | 'draft')}
                    className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">全部状态</option>
                    <option value="published">已发布</option>
                    <option value="draft">草稿</option>
                  </select>
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="all">全部分类</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </>
              )}
              <button
                onClick={showRecycleBin ? fetchRecycleBin : fetchNotices}
                className="flex items-center gap-1 px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                刷新
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">公告信息</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">分类</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">状态</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">发布时间</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">标记</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full" />
                        加载中...
                      </div>
                    </td>
                  </tr>
                ) : notices.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                      <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                      暂无公告
                    </td>
                  </tr>
                ) : (
                  notices.map((notice) => (
                    <tr key={notice.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-start gap-2">
                          {notice.is_pinned && <Pin className="w-4 h-4 text-blue-500 mt-1" />}
                          {notice.is_highlighted && <AlertCircle className="w-4 h-4 text-red-500 mt-1" />}
                          <div>
                            <p className="font-medium text-gray-900 line-clamp-1">{notice.title}</p>
                            <p className="text-sm text-gray-500 line-clamp-1">{notice.summary}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs">
                          {notice.category}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {getStatusBadge(notice.status)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {showRecycleBin ? formatDate(notice.deleted_at || '') : formatDate(notice.publish_time)}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {notice.is_pinned && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs">置顶</span>
                          )}
                          {notice.is_highlighted && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs">高亮</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {showRecycleBin ? (
                            <button
                              onClick={() => { setRestoreTarget(notice); setShowRestoreModal(true); }}
                              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                              title="恢复"
                            >
                              <RotateCcw className="w-4 h-4" />
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => togglePin(notice)}
                                className={`p-1.5 rounded transition-colors ${
                                  notice.is_pinned
                                    ? 'text-blue-600 bg-blue-50 hover:bg-blue-100'
                                    : 'text-gray-400 hover:text-blue-600 hover:bg-blue-50'
                                }`}
                                title={notice.is_pinned ? '取消置顶' : '置顶'}
                              >
                                {notice.is_pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => toggleStatus(notice)}
                                className={`p-1.5 rounded transition-colors ${
                                  notice.status === 'published'
                                    ? 'text-green-600 bg-green-50 hover:bg-green-100'
                                    : 'text-gray-400 hover:text-green-600 hover:bg-green-50'
                                }`}
                                title={notice.status === 'published' ? '下架' : '发布'}
                              >
                                {notice.status === 'published' ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                              </button>
                              <button
                                onClick={() => openEditModal(notice)}
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                                title="编辑"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => { setDeleteTarget(notice); setShowDeleteModal(true); }}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                title="删除"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                共 {totalCount} 条，第 {currentPage}/{totalPages} 页
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const page = i + 1;
                  return (
                    <button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1.5 rounded text-sm font-medium ${
                        currentPage === page
                          ? 'bg-blue-600 text-white'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      {page}
                    </button>
                  );
                })}
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <AnimatePresence>
        {showEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
            >
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <h2 className="text-lg font-bold text-gray-900">
                  {editingNotice ? '编辑公告' : '新增公告'}
                </h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      公告标题 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="请输入公告标题"
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">公告摘要</label>
                    <textarea
                      value={formData.summary}
                      onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                      placeholder="请输入公告摘要（首页列表显示）"
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">公告内容</label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                      placeholder="请输入公告正文内容（支持换行、链接等基础格式）"
                      rows={6}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                    />
                    <p className="text-xs text-gray-500 mt-1">支持换行、链接等基础格式，不支持HTML脚本</p>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">分类</label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">发布时间</label>
                      <input
                        type="datetime-local"
                        value={formData.publish_time}
                        onChange={(e) => setFormData({ ...formData, publish_time: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">链接类型</label>
                      <select
                        value={formData.link_type}
                        onChange={(e) => setFormData({ ...formData, link_type: e.target.value as 'internal' | 'external' })}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="internal">站内链接</option>
                        <option value="external">外部链接</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">链接地址</label>
                      <input
                        type="text"
                        value={formData.link}
                        onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                        placeholder={formData.link_type === 'internal' ? '/notice/123' : 'https://...'}
                        className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_pinned}
                        onChange={(e) => setFormData({ ...formData, is_pinned: e.target.checked })}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700">置顶</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.is_highlighted}
                        onChange={(e) => setFormData({ ...formData, is_highlighted: e.target.checked })}
                        className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                      />
                      <span className="text-sm text-gray-700">高亮显示</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.status === 'published'}
                        onChange={(e) => setFormData({ ...formData, status: e.target.checked ? 'published' : 'draft' })}
                        className="w-4 h-4 text-green-600 rounded focus:ring-green-500"
                      />
                      <span className="text-sm text-gray-700">立即发布</span>
                    </label>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleSave}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingNotice ? '保存修改' : '创建公告'}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showDeleteModal && deleteTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl p-6 max-w-md w-full"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">确认删除</h3>
              </div>
              <p className="text-gray-600 mb-6">
                确定要删除公告「{deleteTarget.title}」吗？删除后可在回收站恢复。
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  确认删除
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showRestoreModal && restoreTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl p-6 max-w-md w-full"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                  <RotateCcw className="w-5 h-5 text-green-600" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">确认恢复</h3>
              </div>
              <p className="text-gray-600 mb-6">
                确定要恢复公告「{restoreTarget.title}」吗？恢复后将回到公告栏。
              </p>
              <div className="flex items-center justify-end gap-3">
                <button
                  onClick={() => setShowRestoreModal(false)}
                  className="px-4 py-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  取消
                </button>
                <button
                  onClick={handleRestore}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  确认恢复
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
